import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { resolveApiMediaUrl } from '../../api/client.js';
import { isVitrineItemEnabled, normalizeVitrineProductIds } from '../../theme/storeHomeLayout.js';
import { firstDisplayImageRaw } from '../../utils/productImage.js';
import { StoreHeroCarousel } from './StoreHeroCarousel.jsx';
import { normalizeHeroSlide } from '../../theme/storeHero.js';

function productDisplayImageUrl(p, productImageCtx) {
  const raw = firstDisplayImageRaw(p, productImageCtx);
  return raw ? resolveApiMediaUrl(raw) : '';
}

function HorizontalScroller({ children, className = '' }) {
  return (
    <div
      className={`flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 pt-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * Vitrine modular (hero + secções) — dados vêm de `parseStoreHomeLayout`.
 * @param {{
 *   layout: { hero?: unknown[]; sections?: unknown[] },
 *   slug: string,
 *   catalogById: Record<number, unknown>,
 *   productImageCtx?: import('../../utils/productImage.js').ProductImageContext,
 *   pricingHidden: boolean,
 *   formatBrl: (n: number) => string,
 *   onNavigateToSection: (secao: string) => void,
 *   onOpenProduct: (id: number) => void,
 *   autoplaySeconds?: number,
 *   heroSlidesOverride?: unknown[],
 *   hideHero?: boolean,
 * }} props
 */
export function StoreHomeLayout({
  layout,
  slug,
  catalogById,
  productImageCtx,
  pricingHidden,
  formatBrl,
  onNavigateToSection,
  onOpenProduct,
  autoplaySeconds = 5,
  heroSlidesOverride,
  hideHero = false,
}) {
  const heroSlides = useMemo(() => {
    const raw =
      Array.isArray(heroSlidesOverride) && heroSlidesOverride.length > 0
        ? heroSlidesOverride
        : layout?.hero && Array.isArray(layout.hero)
          ? layout.hero
          : [];
    const out = [];
    for (const x of raw) {
      const n = normalizeHeroSlide(x);
      if (n && n.enabled && n.image_url) out.push(n);
    }
    return out;
  }, [layout?.hero, heroSlidesOverride]);

  const sections = useMemo(() => {
    const arr = layout?.sections && Array.isArray(layout.sections) ? layout.sections : [];
    return arr.filter(
      (x) => x && typeof x === 'object' && typeof x.type === 'string' && isVitrineItemEnabled(x),
    );
  }, [layout?.sections]);

  const resolveProduct = useCallback(
    (id) => {
      const n = Number(id);
      if (!Number.isFinite(n) || n < 1) return null;
      return catalogById[n] || null;
    },
    [catalogById],
  );

  const storeRoot = useMemo(() => `/${encodeURIComponent(slug)}`, [slug]);

  return (
    <div className="space-y-4">
      {!hideHero && heroSlides.length > 0 ? (
        <StoreHeroCarousel
          slides={heroSlides}
          autoplaySeconds={autoplaySeconds}
          onNavigateToSection={onNavigateToSection}
          onOpenProduct={onOpenProduct}
        />
      ) : null}

      {sections.map((sec, si) => {
        const type = String(sec.type || '').trim();
        if (type === 'category_strip') {
          const title = String(sec.title || 'Categorias');
          const shape = sec.item_shape === 'squircle' ? 'squircle' : 'circle';
          const tileBg = typeof sec.tile_bg === 'string' && sec.tile_bg.trim() ? sec.tile_bg.trim() : '#e0f2fe';
          const items = (Array.isArray(sec.items) ? sec.items : []).filter(
            (it) => it && typeof it === 'object' && isVitrineItemEnabled(it),
          );
          return (
            <section
              key={`cs-${si}`}
              className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm dark:border-zinc-700/80 dark:bg-zinc-900"
            >
              <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-50">{title}</h2>
              <div className="relative mt-3">
                <HorizontalScroller>
                  {items.map((it, ii) => {
                    const label = String(it?.label || '');
                    const rawImg = typeof it?.image_url === 'string' ? it.image_url.trim() : '';
                    const img =
                      rawImg && (/^https?:\/\//i.test(rawImg) || rawImg.startsWith('/'))
                        ? rawImg
                        : rawImg
                          ? resolveApiMediaUrl(rawImg)
                          : '';
                    const secao = it?.secao != null ? String(it.secao) : '';
                    const href = typeof it?.href === 'string' ? it.href.trim() : '';
                    const rounded = shape === 'squircle' ? 'rounded-2xl' : 'rounded-full';
                    const tile = (
                      <div
                        className={`flex h-[88px] w-[88px] shrink-0 snap-start items-center justify-center ${rounded} p-2 shadow-inner sm:h-[100px] sm:w-[100px]`}
                        style={{ backgroundColor: tileBg }}
                      >
                        {img ? (
                          <img src={img} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                        ) : (
                          <span className="text-2xl opacity-40" aria-hidden>
                            ◆
                          </span>
                        )}
                      </div>
                    );
                    const body = (
                      <div className="flex w-[100px] shrink-0 snap-start flex-col items-center gap-2 sm:w-[112px]">
                        {href ? (
                          <a href={href} className="block transition hover:opacity-90" target="_blank" rel="noreferrer">
                            {tile}
                          </a>
                        ) : (
                          <button
                            type="button"
                            className="block text-left transition hover:opacity-90"
                            onClick={() => onNavigateToSection(secao)}
                          >
                            {tile}
                          </button>
                        )}
                        <span className="max-w-[6.5rem] text-center text-[11px] font-semibold leading-tight text-zinc-800 dark:text-zinc-200">
                          {label}
                        </span>
                      </div>
                    );
                    return <div key={ii}>{body}</div>;
                  })}
                </HorizontalScroller>
              </div>
            </section>
          );
        }

        if (type === 'produtos_destaque') {
          const title = String(sec.title || 'Em destaque');
          const limRaw = Number(sec.limit);
          const limit =
            Number.isFinite(limRaw) && limRaw > 0 ? Math.min(48, Math.max(1, Math.floor(limRaw))) : 12;
          const rows = [];
          for (const p of Object.values(catalogById)) {
            if (!p || typeof p !== 'object') continue;
            const id = Number(p.id);
            if (!Number.isFinite(id)) continue;
            const isDest = p.destaque === true || Number(p.destaque) === 1;
            const active = p.ativo === undefined || Number(p.ativo) !== 0;
            if (!isDest || !active) continue;
            rows.push(p);
          }
          rows.sort((a, b) => Number(a.id) - Number(b.id));
          const show = rows.slice(0, limit);
          if (show.length === 0) return null;
          return (
            <section
              key={`pd-${si}`}
              className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm dark:border-zinc-700/80 dark:bg-zinc-900"
            >
              <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-50">{title}</h2>
              <HorizontalScroller className="mt-3">
                {show.map((p) => {
                  const id = Number(p.id);
                  const src = productDisplayImageUrl(p, productImageCtx);
                  const nome = String(p.nome || '');
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onOpenProduct(id)}
                      className="group flex w-28 shrink-0 snap-start flex-col gap-2 text-left sm:w-32"
                    >
                      <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border border-zinc-100 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800">
                        {src ? (
                          <img
                            src={src}
                            alt=""
                            className="max-h-full max-w-full object-contain transition group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs text-zinc-400">Sem imagem</span>
                        )}
                      </div>
                      <span className="line-clamp-2 text-[11px] font-semibold leading-snug text-zinc-800 dark:text-zinc-100">
                        {nome}
                      </span>
                      {!pricingHidden && p.preco != null ? (
                        <span className="text-xs font-bold tabular-nums text-zinc-900 dark:text-white">
                          {formatBrl(Number(p.preco))}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </HorizontalScroller>
            </section>
          );
        }

        if (type === 'product_row') {
          const title = String(sec.title || 'Produtos');
          const ids = normalizeVitrineProductIds(sec.product_ids)
            .filter((e) => e.enabled)
            .map((e) => e.id);
          return (
            <section
              key={`pr-${si}`}
              className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm dark:border-zinc-700/80 dark:bg-zinc-900"
            >
              <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-50">{title}</h2>
              <HorizontalScroller className="mt-3">
                {ids.map((id) => {
                  const p = resolveProduct(id);
                  if (!p) return null;
                  const src = productDisplayImageUrl(p, productImageCtx);
                  const nome = String(p.nome || '');
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onOpenProduct(id)}
                      className="group flex w-28 shrink-0 snap-start flex-col gap-2 text-left sm:w-32"
                    >
                      <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border border-zinc-100 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800">
                        {src ? (
                          <img
                            src={src}
                            alt=""
                            className="max-h-full max-w-full object-contain transition group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs text-zinc-400">Sem imagem</span>
                        )}
                      </div>
                      <span className="line-clamp-2 text-[11px] font-semibold leading-snug text-zinc-800 dark:text-zinc-100">
                        {nome}
                      </span>
                      {!pricingHidden && p.preco != null ? (
                        <span className="text-xs font-bold tabular-nums text-zinc-900 dark:text-white">
                          {formatBrl(Number(p.preco))}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </HorizontalScroller>
            </section>
          );
        }

        if (type === 'card_grid') {
          const title = String(sec.title || '');
          const cards = Array.isArray(sec.cards) ? sec.cards : [];
          const cols = Number(sec.columns) === 2 ? 2 : 4;
          const gridCls = cols === 2 ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-3 lg:grid-cols-4';
          return (
            <section
              key={`cg-${si}`}
              className="rounded-2xl border border-zinc-200/80 bg-zinc-100/80 p-3 shadow-sm dark:border-zinc-700/80 dark:bg-zinc-950/80"
            >
              {title ? (
                <h2 className="mb-3 px-1 text-base font-bold text-zinc-900 dark:text-zinc-50">{title}</h2>
              ) : null}
              <div className={gridCls}>
                {cards.map((card, ci) => {
                  if (!isVitrineItemEnabled(card)) return null;
                  const ct = String(card?.title || '');
                  const variant = String(card?.variant || 'quad');
                  const footerLabel = typeof card?.footer_link_label === 'string' ? card.footer_link_label.trim() : '';
                  const footerHref = typeof card?.footer_href === 'string' ? card.footer_href.trim() : '';
                  if (variant === 'spotlight') {
                    const imgRaw = typeof card?.image_url === 'string' ? card.image_url.trim() : '';
                    const img =
                      imgRaw && (/^https?:\/\//i.test(imgRaw) || imgRaw.startsWith('/'))
                        ? imgRaw
                        : imgRaw
                          ? resolveApiMediaUrl(imgRaw)
                          : '';
                    const subtitle = typeof card?.subtitle === 'string' ? card.subtitle : '';
                    const href = typeof card?.href === 'string' ? card.href.trim() : '';
                    const sponsored = Boolean(card?.sponsored);
                    const pid = Number(card?.product_id);
                    const p = Number.isFinite(pid) && pid >= 1 ? resolveProduct(pid) : null;
                    const priceLabel =
                      typeof card?.price_label === 'string' && card.price_label.trim()
                        ? card.price_label.trim()
                        : p && !pricingHidden && p.preco != null
                          ? formatBrl(Number(p.preco))
                          : '';
                    return (
                      <div
                        key={ci}
                        className="flex min-h-[220px] flex-col rounded-xl border border-zinc-200/90 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
                      >
                        <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-50">{ct}</h3>
                        {sponsored ? (
                          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                            Patrocinado
                          </p>
                        ) : null}
                        <div className="mt-2 flex flex-1 flex-col">
                          {href ? (
                            <a href={href} target="_blank" rel="noreferrer" className="block flex-1">
                              {img ? (
                                <img src={img} alt="" className="mx-auto max-h-36 object-contain" loading="lazy" />
                              ) : null}
                            </a>
                          ) : p ? (
                            <button type="button" onClick={() => onOpenProduct(Number(p.id))} className="block flex-1 text-left">
                              {img || productDisplayImageUrl(p, productImageCtx) ? (
                                <img
                                  src={img || productDisplayImageUrl(p, productImageCtx)}
                                  alt=""
                                  className="mx-auto max-h-36 object-contain"
                                  loading="lazy"
                                />
                              ) : null}
                            </button>
                          ) : img ? (
                            <img src={img} alt="" className="mx-auto max-h-36 object-contain" loading="lazy" />
                          ) : null}
                          {subtitle ? (
                            <p className="mt-2 line-clamp-3 text-xs text-zinc-600 dark:text-zinc-400">{subtitle}</p>
                          ) : null}
                          {p && !subtitle ? (
                            <p className="mt-2 line-clamp-3 text-xs text-zinc-700 dark:text-zinc-300">{String(p.nome || '')}</p>
                          ) : null}
                          {priceLabel ? (
                            <p className="mt-2 text-sm font-bold tabular-nums text-zinc-900 dark:text-white">{priceLabel}</p>
                          ) : null}
                        </div>
                      </div>
                    );
                  }
                  if (variant === 'promo') {
                    const bg = typeof card?.promo_bg === 'string' && card.promo_bg.trim() ? card.promo_bg.trim() : '#ea580c';
                    const body = typeof card?.body === 'string' ? card.body : '';
                    const imgRaw = typeof card?.image_url === 'string' ? card.image_url.trim() : '';
                    const img =
                      imgRaw && (/^https?:\/\//i.test(imgRaw) || imgRaw.startsWith('/'))
                        ? imgRaw
                        : imgRaw
                          ? resolveApiMediaUrl(imgRaw)
                          : '';
                    const href = typeof card?.href === 'string' ? card.href.trim() : '';
                    const cta = typeof card?.cta === 'string' && card.cta.trim() ? card.cta.trim() : 'Saiba mais';
                    return (
                      <div
                        key={ci}
                        className="flex min-h-[220px] flex-col rounded-xl border border-zinc-200/90 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
                      >
                        <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-50">{ct}</h3>
                        <div
                          className="mt-2 flex flex-1 flex-col justify-between rounded-lg p-3 text-white"
                          style={{ backgroundColor: bg }}
                        >
                          {body ? <p className="text-sm font-medium leading-snug">{body}</p> : null}
                          {img ? <img src={img} alt="" className="mx-auto mt-2 max-h-28 object-contain" loading="lazy" /> : null}
                          {href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-3 inline-flex w-fit rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold text-zinc-900"
                            >
                              {cta}
                            </a>
                          ) : null}
                        </div>
                      </div>
                    );
                  }
                  const pids = normalizeVitrineProductIds(card?.product_ids)
                    .filter((e) => e.enabled)
                    .map((e) => e.id)
                    .slice(0, 4);
                  return (
                    <div
                      key={ci}
                      className="flex min-h-[220px] flex-col rounded-xl border border-zinc-200/90 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
                    >
                      <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-50">{ct}</h3>
                      <div className="mt-2 grid flex-1 grid-cols-2 gap-2">
                        {pids.map((id) => {
                          const p = resolveProduct(id);
                          if (!p) return <div key={id} />;
                          const src = productDisplayImageUrl(p, productImageCtx);
                          return (
                            <button
                              key={id}
                              type="button"
                              onClick={() => onOpenProduct(id)}
                              className="flex flex-col gap-1 text-left"
                            >
                              <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg bg-zinc-50 dark:bg-zinc-800">
                                {src ? (
                                  <img src={src} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                                ) : null}
                              </div>
                              <span className="line-clamp-2 text-[10px] font-medium leading-tight text-zinc-700 dark:text-zinc-300">
                                {String(p.nome || '')}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {footerLabel && footerHref ? (
                        <a
                          href={footerHref}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 text-xs font-semibold text-[var(--st-700)] underline-offset-2 hover:underline dark:text-[var(--st-400)]"
                        >
                          {footerLabel}
                        </a>
                      ) : footerLabel ? (
                        <Link
                          to={storeRoot}
                          className="mt-2 text-xs font-semibold text-[var(--st-700)] underline-offset-2 hover:underline dark:text-[var(--st-400)]"
                        >
                          {footerLabel}
                        </Link>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        }

        return null;
      })}
    </div>
  );
}
